import { makeStyles, createStyles, Theme, Grid, Typography, OutlinedInput } from "@material-ui/core";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import fallback_img from "../../../../../images/fallback_item.png";
import { IKImage } from "imagekitio-react";
import {
  selectComponentDimensions,
  selectGroupItems,
  selectGroupItemsEntities,
  selectLandingFromGroup,
} from "../../../../../redux/slices/landingSlice";
import useLandingFunctions from "../../../../../hooks/useLandingFunctions";
import { PFXUpsellingComponent } from "../../../../../models/Interfaces";
import { selectIsMobile } from "../../../../../redux/slices/cleverlynkSlice";
import { PFXCategoryItem, selectItemsEntities } from "../../../../../redux/slices/templateSlice";
import { TITLE_FONTSIZE_BASE } from "../../../../../pages/Landing";

interface IMultiple {
  data: PFXUpsellingComponent;
  loading: boolean;
  id: string;
}

const Multiple = ({ data, id, loading }: IMultiple) => {
  const [selectedItem, setSelectedItem] = useState<number>(0);

  const { width, height } = useSelector(selectComponentDimensions);
  const { handleGoToItem } = useLandingFunctions();
  const isMobile = useSelector(selectIsMobile);
  const allItems = useSelector(selectItemsEntities);

  const groupItems = useSelector(selectGroupItemsEntities);
  const landingFromGroup = useSelector(selectLandingFromGroup);

  const items = useMemo(() => {
    if ((data?.productIds ?? []).length === 0) return undefined;
    else if (landingFromGroup) {
      return (data?.productIds ?? []).map(pid => groupItems[pid?.elementId ?? ""]).filter(i => !!i);
    } else return (data?.productIds ?? []).map(pid => allItems[pid?.elementId ?? ""]).filter(i => !!i);
  }, [allItems, data.productIds, landingFromGroup, groupItems]);

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      container: {
        minHeight: height / 3,
        width,
      },
      root: {
        backgroundColor: data?.backgroundColor ?? "#f9f9f9",
        paddingTop: 20,
      },
      grid: {
        padding: isMobile ? 20 : 0,
      },
      mobileitem: {
        height: 100,
        minWidth: 130,
        backgroundColor: "white",
        borderRadius: 9,
        position: "relative",
        boxShadow: "1pt 1pt 4px -2px #000000b5",
        transition: "height 400ms",
        marginRight: 12,
        marginTop: 12,
        "&.selected": {
          marginRight: 0,
          marginTop: 0,
          height: 300,
          width: 300,
        },
      },
      overlay: {
        width: "100%",
        position: "absolute",
        height: "100%",
        zIndex: 10,
        borderRadius: 9,
        transition: "opacity 400ms 200ms",
        opacity: 0,
        background:
          "linear-gradient(0deg, rgba(0,0,0,0.7287289915966386) 0%, rgba(255,255,255,0) 80%, rgba(255,255,255,0) 100%)",
        "&.selected": {
          opacity: 0,
        },
      },
      image: {
        width: "100%",
        height: "100%",
        zIndex: 9,
        objectFit: "cover",
        borderRadius: 9,
      },
      text: {
        position: "absolute",
        width: "100%",
        minHeight: 40,
        backgroundColor: data?.title?.color ?? "#fff",
        color: data?.backgroundColor,
        opacity: 0,
        bottom: 0,
        borderRadius: "0px 0px 9px 9px",
        transition: "opacity 400ms",
        zIndex: 11,
        "&.selected": {
          opacity: 1,
        },
      },
      description: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "100%",
        display: "block",
        paddingBottom: 8,
        paddingLeft: 10,
      },
      desktopitem: {
        position: "relative",
        boxShadow: "1pt 1pt 4px -2px #000000b5",
        borderRadius: 9,
        height: "100%",
      },
      smallDesktopItemGrid: {
        maxHeight: "50%",
      },
      title: {
        fontSize: `${data.title?.fontSizeMultiplier ?? 1 * TITLE_FONTSIZE_BASE}rem`,
        color: data.title?.color ?? "#1a1a1a",
        textAlign: (data.title?.alignment ?? "center") as any,
        fontWeight: data.title?.fontWeight as any,
        fontFamily: data.title?.fontFamily,
        "&.input": {
          height: "100%",
        },
      },
      titleDiv: {
        minHeight: `calc(${height / 8}px - 13px)`,
        width: "100%",
        borderRadius: 5,
      },
      titleInput: {
        minHeight: `calc(${height / 8}px - 13px)`,
        width: "100%",
        borderRadius: 5,
      },
    })
  );

  const classes = useStyles();

  const shouldRenderTitle = useMemo(() => !!data.title && (data.title.content ?? "").trim().length > 0, [data]);

  const desktop = useMemo(() => {
    const imagePaths = (items ?? []).map(i => {
      if (!i || !i?.images) return undefined;
      return i?.images && i.images.length > 0 && i.images[0]
        ? i.images[0].split("cleverlynk-imgs.s3.amazonaws.com")[1]
        : undefined;
    });

    const renderItem = (path: string | undefined, item: PFXCategoryItem | undefined, selected?: boolean) => (
      <React.Fragment>
        <div
          className={`${selected ? "selected" : ""} ${classes.text}`}
          onClick={() => (!!item ? handleGoToItem(item) : null)}>
          <Typography variant="body1" style={{ paddingLeft: 10, paddingTop: 8 }}>
            {item?.name}
          </Typography>
          <Typography variant="caption" className={classes.description}>
            {item?.description}
          </Typography>
        </div>
        {path ? (
          <IKImage
            id={`expanded-${items![0]?.id}-${path}`}
            key={`expanded-${items![0]?.id}-${path}`}
            className={classes.image}
            style={{
              position: "relative",
            }}
            path={path}
            onClick={() => (!!item ? handleGoToItem(item) : null)}
            transformation={[
              {
                height: 350,
                width: 350,
              },
            ]}
            loading="lazy"
            lqip={{ active: true, quality: 20, blur: 10 }}
          />
        ) : (
          <img
            src={fallback_img}
            style={{
              margin: "0 auto",
            }}
            className={classes.image}
            alt={"fallback item"}
          />
        )}
      </React.Fragment>
    );

    return (
      <Grid item container spacing={2} justifyContent="center" style={{ margin: 0, width: "100%" }}>
        {(items ?? []).length > 0 && (
          <Grid item xs={5} style={{ paddingLeft: 2 }}>
            <div className={classes.desktopitem}>
              {items && items[0] ? renderItem(imagePaths[0], items[0] as any, true) : null}
            </div>
          </Grid>
        )}
        <Grid item container xs={5} spacing={1}>
          {(items ?? []).length > 1 && (
            <Grid item xs={6} className={classes.smallDesktopItemGrid}>
              <div className={classes.desktopitem}>
                {items && items[1] ? renderItem(imagePaths[1], items[1] as any) : null}
              </div>
            </Grid>
          )}
          {(items ?? []).length > 2 && (
            <Grid item xs={6} className={classes.smallDesktopItemGrid}>
              <div className={classes.desktopitem}>
                {items && items[2] ? renderItem(imagePaths[2], items[2] as any) : null}
              </div>
            </Grid>
          )}
          {(items ?? []).length > 3 && (
            <Grid item xs={6} className={classes.smallDesktopItemGrid}>
              <div className={classes.desktopitem}>
                {items && items[3] ? renderItem(imagePaths[3], items[3] as any) : null}
              </div>
            </Grid>
          )}
          {(items ?? []).length > 4 && (
            <Grid item xs={6} className={classes.smallDesktopItemGrid}>
              <div className={classes.desktopitem}>
                {items && items[4] ? renderItem(imagePaths[4], items[4] as any) : null}
              </div>
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  }, [classes.desktopitem, items, selectedItem]);

  const mobile = useMemo(() => {
    const sitem = (items ?? [])[selectedItem];
    const selectedPath = () => {
      if (!sitem || !sitem?.images) return undefined;
      return sitem?.images && sitem.images.length > 0 && sitem.images[0]
        ? sitem.images[0].split("cleverlynk-imgs.s3.amazonaws.com")[1]
        : undefined;
    };
    const sp = selectedPath();
    return (
      <Grid item container direction="column" alignItems="center" style={{ marginTop: 10 }}>
        <Grid
          item
          className={`selected ${classes.mobileitem}`}
          onClick={() => (!!sitem ? handleGoToItem(sitem) : null)}>
          <div className={`selected ${classes.text}`}>
            <Typography variant="body1" style={{ paddingLeft: 10, paddingTop: 8, fontSize: "0.9rem" }}>
              {sitem?.name}
            </Typography>
            <Typography variant="caption" className={classes.description}>
              {sitem?.description}
            </Typography>
          </div>
          {sp ? (
            <IKImage
              id={`multiple-selected-${sitem?.id}-${sp}`}
              key={`multiple-selected-${sitem?.id}-${sp}`}
              className={classes.image}
              style={{
                position: "relative",
              }}
              path={sp}
              transformation={[
                {
                  height: 350,
                  width: 350,
                },
              ]}
              loading="lazy"
              lqip={{ active: true, quality: 20, blur: 10 }}
            />
          ) : (
            <img
              src={fallback_img}
              style={{
                margin: "0 auto",
              }}
              className={classes.image}
              alt={"fallback item"}
            />
          )}
        </Grid>
        <Grid item container style={{ overflowX: "auto", paddingBottom: 20 }}>
          <Grid item container alignItems="flex-start" wrap="nowrap">
            {items?.map((i, index) => {
              const p = () => {
                if (!i || !i?.images) return undefined;
                return i?.images && i.images.length > 0 && i.images[0]
                  ? i.images[0].split("cleverlynk-imgs.s3.amazonaws.com")[1]
                  : undefined;
              };
              const path = p();
              return (
                <Grid item className={` ${classes.mobileitem}`} onClick={() => setSelectedItem(index)}>
                  <div className={` ${classes.text}`}>
                    <Typography variant="body1" style={{ paddingLeft: 10, paddingTop: 8, fontSize: "0.9rem" }}>
                      {i?.name}
                    </Typography>
                    <Typography variant="caption" className={classes.description}>
                      {i?.description}
                    </Typography>
                  </div>
                  {path ? (
                    <IKImage
                      id={`multiple-${i?.id}-${path}`}
                      key={`multiple-${i?.id}-${path}`}
                      className={classes.image}
                      style={{
                        position: "relative",
                      }}
                      path={path}
                      transformation={[
                        {
                          height: 350,
                          width: 350,
                        },
                      ]}
                      loading="lazy"
                      lqip={{ active: true, quality: 20, blur: 10 }}
                    />
                  ) : (
                    <img
                      src={fallback_img}
                      style={{
                        margin: "0 auto",
                      }}
                      className={classes.image}
                      alt={"fallback item"}
                    />
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    );
  }, [classes.mobileitem, items, selectedItem]);

  return (
    <Grid container justifyContent="center" alignItems="center" className={classes.root}>
      <div id={`landing-component-${id}`} className={classes.container}>
        <Grid container className={classes.grid} justifyContent="center">
          {shouldRenderTitle && (
            <Grid item xs={12} className={classes.titleDiv} id={`title-${id}`}>
              <Typography className={classes.title}>{data.title?.content}</Typography>
            </Grid>
          )}
          {isMobile ? mobile : desktop}
        </Grid>
      </div>
    </Grid>
  );
};
export default Multiple;
